import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useChatWebSocket } from './ChatWebSocketContext';

interface GlobalPresenceContextValue {
  isUserOnline: (userId: number | string | null | undefined) => boolean;
  /** prod1: время выхода из сети из события presence (websocket-hub), если пришло в этой сессии. */
  getLastSeen: (userId: number | string | null | undefined) => string | null;
  /** true, когда получен снимок онлайна от websocket-hub (до этого «не в сети» не показываем как факт). */
  isReady: boolean;
  onlineUserIds: ReadonlySet<string>;
}

const GlobalPresenceContext = createContext<GlobalPresenceContextValue>({
  isUserOnline: () => false,
  getLastSeen: () => null,
  isReady: false,
  onlineUserIds: new Set(),
});

export const useGlobalPresence = () => useContext(GlobalPresenceContext);

interface GlobalPresenceProviderProps {
  children: React.ReactNode;
}

export const GlobalPresenceProvider: React.FC<GlobalPresenceProviderProps> = ({
  children,
}) => {
  const { subscribeGlobal, isConnected } = useChatWebSocket();
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const [lastSeen, setLastSeen] = useState<Record<string, string>>({});
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!isConnected) {
      setOnlineUserIds(new Set());
      setIsReady(false);
      return;
    }

    return subscribeGlobal((event) => {
      if (event.type === 'presence_snapshot') {
        const raw = event.data.online_user_ids;
        if (!Array.isArray(raw)) {
          return;
        }
        setOnlineUserIds(new Set(raw.map((id) => String(id))));
        setIsReady(true);
        return;
      }

      if (event.type === 'presence') {
        const userId = event.data.user_id;
        const online = event.data.online;
        if (userId == null) {
          return;
        }
        const id = String(userId);
        const seenAt = event.data.last_seen_at;
        if (!online && typeof seenAt === 'string' && seenAt) {
          setLastSeen((prev) => ({ ...prev, [id]: seenAt }));
        }
        setOnlineUserIds((prev) => {
          const next = new Set(prev);
          if (online) {
            next.add(id);
          } else {
            next.delete(id);
          }
          return next;
        });
      }
    });
  }, [isConnected, subscribeGlobal]);

  const isUserOnline = useCallback(
    (id: number | string | null | undefined) => {
      if (id === null || id === undefined) return false;
      return onlineUserIds.has(String(id));
    },
    [onlineUserIds],
  );

  const getLastSeen = useCallback(
    (id: number | string | null | undefined) => {
      if (id === null || id === undefined) return null;
      return lastSeen[String(id)] ?? null;
    },
    [lastSeen],
  );

  const value = useMemo(
    () => ({ isUserOnline, getLastSeen, isReady, onlineUserIds }),
    [isUserOnline, getLastSeen, isReady, onlineUserIds],
  );

  return (
    <GlobalPresenceContext.Provider value={value}>
      {children}
    </GlobalPresenceContext.Provider>
  );
};
